import React, { FC } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import useChartData from '../../hooks/useCalculateChartData';
import { ISurveyResponse } from '../../shared/interfaces/ISurveyResponse';
import { Box, Divider, Typography, useMediaQuery, useTheme } from '@mui/material';

interface Props {
  surveyResponse: ISurveyResponse[];
  type: string;
  mode: string;
  x?: number;
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  percent?: number;
  index?: number;
  name?: string;
  surveys?: number;
  customAnswers?: {
    answerOne: string;
    answerTwo: string;
    answerThree: string;
    answerFour: string;
    answerFive: string;
  };
}

const PieChartSurvey: FC<Props> = ({ surveyResponse, type, mode, customAnswers }) => {
  const theme = useTheme();
  const chartData = useChartData(surveyResponse, type, mode, customAnswers);
  const chartArrayOne = chartData.slice(0, 5);
  const chartArrayTwo = chartData.slice(5, 10);
  const chartArrayThree = chartData.slice(10, 12);
  const questionList = surveyResponse.map((survey, index) => survey.question);
  const isSmallScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    name,
    surveys,
  }: Props) => {
    if (
      cx === undefined ||
      cy === undefined ||
      midAngle === undefined ||
      innerRadius === undefined ||
      outerRadius === undefined ||
      percent === undefined ||
      index === undefined
    ) {
      return null;
    }
    const radius = !isSmallScreen
      ? innerRadius + (outerRadius - innerRadius)
      : innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = isSmallScreen
      ? cx + radius * Math.cos(-midAngle * RADIAN)
      : cx + radius * Math.cos(-midAngle * RADIAN) + 10;
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const cos = Math.cos(-RADIAN * midAngle);
    const textAnchor = cos >= 0 ? 'start' : 'end';
    const textAnchorSmallScreen = x > cx ? 'start' : 'end';

    if (surveys === 0) return null;

    return (
      <Typography
        component="text"
        x={x}
        y={y}
        textAnchor={!isSmallScreen ? textAnchorSmallScreen : textAnchor}
        fill="black"
        dominantBaseline="central"
      >
        {!isSmallScreen ? (
          `${(percent * 100).toFixed(0)}%`
        ) : (
          <>
            <Typography
              component="tspan"
              x={x}
              dy="0"
              fontSize={isMediumScreen ? 12 : 18}
            >
              {name}
            </Typography>
            <Typography
              component="tspan"
              x={x}
              dy="1.2em"
              fontSize={isMediumScreen ? 12 : 18}
            >
              {surveys} ({(percent * 100).toFixed(0)}%)
            </Typography>
          </>
        )}
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-around',
        width: '100%',
        height: 'auto',
        paddingRight: '10px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexWrap: 'nowrap',
          width: '100%',
        }}
      >
        <Box
          sx={{
            height: '50px',
            minHeight: '50px',
          }}
        >
          <Typography
            sx={{
              textAlign: 'center',
            }}
          >
            {questionList[0]}
          </Typography>
        </Box>
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <PieChart
            width={400}
            height={400}
          >
            <Pie
              data={chartArrayOne}
              cx="50%"
              cy="50%"
              innerRadius={isSmallScreen ? 40 : 0}
              outerRadius={80}
              fill="#8884d8"
              dataKey="surveys"
              labelLine={false}
              label={(props) => renderCustomizedLabel({ ...props, theme })}
            >
              {chartArrayOne.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Divider
        orientation="vertical"
        variant="middle"
        flexItem
        sx={{ height: 'auto' }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexWrap: 'nowrap',
          width: '100%',
        }}
      >
        <Box
          sx={{
            height: '50px',
            minHeight: '50px',
          }}
        >
          <Typography
            sx={{
              textAlign: 'center',
            }}
          >
            {questionList[1]}
          </Typography>
        </Box>
        <ResponsiveContainer
          width="100%"
          height={300}
        >
          <PieChart>
            <Pie
              data={chartArrayTwo}
              cx="50%"
              cy="50%"
              innerRadius={isSmallScreen ? 40 : 0}
              outerRadius={80}
              fill="#8884d8"
              dataKey="surveys"
              labelLine={false}
              label={(props) => renderCustomizedLabel({ ...props, theme })}
            >
              {chartArrayTwo.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Divider
        orientation="vertical"
        variant="middle"
        flexItem
        sx={{ height: 'auto' }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexWrap: 'nowrap',
          width: '100%',
        }}
      >
        <Box
          sx={{
            height: '50px',
            minHeight: '50px',
          }}
        >
          <Typography
            sx={{
              textAlign: 'center',
            }}
          >
            {questionList[2]}
          </Typography>
        </Box>
        <ResponsiveContainer
          width="100%"
          height={300}
        >
          <PieChart>
            <Pie
              data={chartArrayThree}
              cx="50%"
              cy="50%"
              innerRadius={isSmallScreen ? 40 : 0}
              outerRadius={80}
              fill="#8884d8"
              dataKey="surveys"
              labelLine={false}
              label={(props) => renderCustomizedLabel({ ...props, theme })}
            >
              {chartArrayThree.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default PieChartSurvey;
